import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { Box, Divider, Grid, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/system';

import SbButton from '@Components/Button';
import Modal from '@Components/Modal';

import { ShipmentObj } from '@Reducers/Invoice/Type';
import {
  getAccountUnbilledCreditList,
  getAccountUnbilledDebitList,
  getNonAccountUnbilledCreditList,
  getNonAccountUnbilledDebitList,
} from '@Reducers/InvoiceList/actions';
import {
  sendAccountCreditDataTo365,
  sendAccountDebitDataTo365,
  sendNonAccountCreditDataTo365,
  sendNonAccountDebitDataTo365,
} from '@Reducers/d365';
import { AppDispatch, RootStateType } from '@Store';

import { formatNumber, useLocale } from '../../../../utils/formatNumber';
import { AccountHolderUnbilledData } from '../../Type';
import {
  CreditDebitConfig,
  CreditDebitFilterType,
  CreditDebitNoteType,
  CreditDebitSearchFilterList,
  NonAccountCreditDebitSearchFilterList,
  SelectedCreditDebitParamType,
  buildCreditDebitD365Payload,
  calculateSelectedCreditDebitPricingSum,
  initialCreditDebitConfig,
  isSameCreditDebitEntry,
  updateSelectedCreditDebitRowsList,
  validateCreditDebitConfig,
} from '../utils/creditDebitNoteUtils';
import CreditDebitConfigSection from './CreditDebitConfigSection';
import CreditDebitShipmentSection from './CreditDebitShipmentSection';

export interface CreditDebitNoteModalProps {
  open: boolean;
  onClose: () => void;
  noteType: CreditDebitNoteType;
  accountType?: 'account' | 'non-account';
  activeSystemList: { display: string; value: string }[];
  isSubmitting: boolean;
  onSubmitNote: (config: CreditDebitConfig, rows: SelectedCreditDebitParamType[], onSuccess?: () => void) => void;
  initialFilterSelectValue: string;
  initialFilterText: string;
  initialDateRange: { startDate: Date; endDate: Date };
  initialSystem: string[];
  onRestorePageFilters: () => void;
}

const CreditDebitNoteModal = React.memo(function CreditDebitNoteModal(props: CreditDebitNoteModalProps) {
  const {
    open,
    onClose,
    noteType,
    accountType = 'account',
    activeSystemList,
    isSubmitting,
    onSubmitNote,
    initialFilterSelectValue,
    initialFilterText,
    initialDateRange: pageDateRange,
    initialSystem,
    onRestorePageFilters,
  } = props;

  const theme = useTheme();
  const locale = useLocale();
  const dispatch = useDispatch<AppDispatch>();

  const isCredit = noteType === 'credit';
  const isNonAccount = accountType === 'non-account';
  const noteTitle = isCredit ? 'Credit Note' : 'Debit Note';
  const modalTitlePrefix = isNonAccount ? 'Non-Account' : 'Account';

  const {
    unBilledList: { list: rawList = [], currentPage = 1, perPage = 10, totalCount = 0 },
    loading: listLoading,
  } = useSelector((state: RootStateType) => state.InvoiceList);
  const list = rawList as unknown as AccountHolderUnbilledData[];

  const { loading: loadingD365 } = useSelector((state: RootStateType) => state?.D365);
  const orgCurrency = useSelector((state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '');

  const [modalFilterSelectValue, setModalFilterSelectValue] = useState(initialFilterSelectValue);
  const [modalFilterText, setModalFilterText] = useState(initialFilterText);
  const [modalDateRange, setModalDateRange] = useState(pageDateRange);
  const [modalSystem, setModalSystem] = useState<string[]>(initialSystem);
  const [hasModifiedModalFilters, setHasModifiedModalFilters] = useState(false);

  const [modalSelectedAccountRows, setModalSelectedAccountRows] = useState<SelectedCreditDebitParamType[]>([]);
  const [expandedInvoices, setExpandedInvoices] = useState<Record<string, boolean>>({});
  const [modalConfig, setModalConfig] = useState<CreditDebitConfig>(initialCreditDebitConfig);

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setModalFilterSelectValue(initialFilterSelectValue);
      setModalFilterText(initialFilterText);
      setModalDateRange(pageDateRange);
      setModalSystem(initialSystem);
      setHasModifiedModalFilters(false);
      setModalSelectedAccountRows([]);
      setExpandedInvoices({});
      setModalConfig(initialCreditDebitConfig);
    }
    wasOpenRef.current = open;
  }, [open, initialFilterSelectValue, initialFilterText, pageDateRange, initialSystem]);

  const [isWyngsMy, setIsWyngsMy] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const subdomain = window.location.hostname.split('.')[0];
      setIsWyngsMy(subdomain === 'wyngs-my');
    }
  }, []);

  const fetchModalData = useCallback(
    (customOverrides: Partial<CreditDebitFilterType> = {}) => {
      const fromDate = moment(customOverrides.fromDate || modalDateRange.startDate).format('DD/MM/YYYY');
      const toDate = moment(customOverrides.toDate || modalDateRange.endDate).format('DD/MM/YYYY');
      const sys = customOverrides.systems !== undefined ? customOverrides.systems : modalSystem;
      const searchKey = customOverrides.searchKey !== undefined ? customOverrides.searchKey : modalFilterSelectValue;
      const searchValue = customOverrides.searchValue !== undefined ? customOverrides.searchValue : modalFilterText;

      const filter: CreditDebitFilterType = {
        currentPage: customOverrides.currentPage || 1,
        perPage: customOverrides.perPage || perPage || 10,
        fromDate,
        toDate,
        systems: sys,
      };

      if (searchValue) {
        filter.searchKey = searchKey;
        filter.searchValue = searchValue;
      }

      if (isNonAccount) {
        if (isCredit) {
          dispatch(getNonAccountUnbilledCreditList(filter));
        } else {
          dispatch(getNonAccountUnbilledDebitList(filter));
        }
      } else {
        if (isCredit) {
          dispatch(getAccountUnbilledCreditList(filter));
        } else {
          dispatch(getAccountUnbilledDebitList(filter));
        }
      }
    },
    [modalDateRange, modalSystem, modalFilterSelectValue, modalFilterText, perPage, isCredit, isNonAccount, dispatch],
  );

  const handleCloseModalClean = useCallback(() => {
    if (isSubmitting) return;
    if (hasModifiedModalFilters) {
      onRestorePageFilters();
    }
    onClose();
  }, [isSubmitting, hasModifiedModalFilters, onRestorePageFilters, onClose]);

  const onModalSearchSelectChange = useCallback((value: string) => {
    setModalFilterSelectValue(value);
  }, []);

  const onModalSearchTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalFilterText(e.target.value);
  }, []);

  const onModalSearchTextSubmit = useCallback(() => {
    setHasModifiedModalFilters(true);
    fetchModalData({ currentPage: 1 });
  }, [fetchModalData]);

  const onModalTextClear = useCallback(() => {
    setModalFilterText('');
    setHasModifiedModalFilters(true);
    fetchModalData({ currentPage: 1, searchValue: '' });
  }, [fetchModalData]);

  const onModalDateChange = useCallback(
    (range: { startDate: Date; endDate: Date }, isOpen: boolean) => {
      if (isOpen) return;
      setModalDateRange(range);
      setHasModifiedModalFilters(true);
      const fromDate = moment(range.startDate).format('DD/MM/YYYY');
      const toDate = moment(range.endDate).format('DD/MM/YYYY');
      fetchModalData({ currentPage: 1, fromDate, toDate });
    },
    [fetchModalData],
  );

  const onModalSystemFilter = useCallback(
    (selected: string[]) => {
      setModalSystem(selected);
      setHasModifiedModalFilters(true);
      fetchModalData({ currentPage: 1, systems: selected });
    },
    [fetchModalData],
  );

  const onModalClear = useCallback(() => {
    setModalFilterText('');
    const defaultSearchKey = isNonAccount ? 'shipment_no' : 'account_no';
    setModalFilterSelectValue(defaultSearchKey);
    setModalSystem([]);
    setModalDateRange(pageDateRange);
    setHasModifiedModalFilters(true);
    const fromDate = moment(pageDateRange.startDate).format('DD/MM/YYYY');
    const toDate = moment(pageDateRange.endDate).format('DD/MM/YYYY');
    fetchModalData({
      currentPage: 1,
      searchValue: '',
      searchKey: defaultSearchKey,
      systems: [],
      fromDate,
      toDate,
    });
  }, [pageDateRange, isNonAccount, fetchModalData]);

  const onModalPageChange = useCallback(
    (_: unknown, newPage: number) => {
      fetchModalData({ currentPage: newPage + 1 });
    },
    [fetchModalData],
  );

  const onModalRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextPerPage = parseInt(event.target.value, 10);
      fetchModalData({ currentPage: 1, perPage: nextPerPage });
    },
    [fetchModalData],
  );

  const toggleExpandInvoice = useCallback((groupKey: string) => {
    setExpandedInvoices(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  }, []);

  const handleSelectInvoiceGroup = useCallback((data: AccountHolderUnbilledData, isChecked: boolean) => {
    const sysId = typeof data._systemId === 'string' ? data._systemId : data._systemId?._id || data.system?._id || '';
    const paramObj: SelectedCreditDebitParamType = {
      accountNumber: data.account?.accountNumber || data.accountNumber || '',
      _systemId: sysId,
      systemName: data.system?.name || (typeof data._systemId === 'object' ? data._systemId?.name : '') || '',
      shipments: isChecked ? data.shipments || [] : [],
      totalAmount: data.totalInvoiceAmount || 0,
      invoiceNumber: data.invoiceNumber || '',
      _userId: data.user?._id,
      uniqueKey: data.uniqueKey,
    };
    setModalSelectedAccountRows(prev => updateSelectedCreditDebitRowsList(prev, paramObj));
  }, []);

  const handleSelectAllInvoicesInModal = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        const nextRows: SelectedCreditDebitParamType[] = list
          .filter(data => (data.shipments?.length || 0) > 0)
          .map(data => {
            const sysId =
              typeof data._systemId === 'string' ? data._systemId : data._systemId?._id || data.system?._id || '';
            return {
              accountNumber: data.account?.accountNumber || data.accountNumber || '',
              _systemId: sysId,
              systemName: data.system?.name || (typeof data._systemId === 'object' ? data._systemId?.name : '') || '',
              shipments: data.shipments || [],
              totalAmount: data.totalInvoiceAmount || 0,
              invoiceNumber: data.invoiceNumber || '',
              _userId: data.user?._id,
              uniqueKey: data.uniqueKey,
            };
          });
        setModalSelectedAccountRows(nextRows);
      } else {
        setModalSelectedAccountRows([]);
      }
    },
    [list],
  );

  const handleToggleSingleShipment = useCallback(
    (data: AccountHolderUnbilledData, shipment: ShipmentObj, isChecked: boolean) => {
      setModalSelectedAccountRows(prev => {
        const existingEntry = prev.find(item => isSameCreditDebitEntry(item, data));
        const currentShipments = existingEntry?.shipments || [];
        const nextShipments = isChecked
          ? currentShipments.some(s => s._id === shipment._id)
            ? currentShipments
            : [...currentShipments, shipment]
          : currentShipments.filter(s => s._id !== shipment._id);

        const sysId =
          typeof data._systemId === 'string' ? data._systemId : data._systemId?._id || data.system?._id || '';
        const paramObj: SelectedCreditDebitParamType = {
          accountNumber: data.account?.accountNumber || data.accountNumber || '',
          _systemId: sysId,
          systemName: data.system?.name || (typeof data._systemId === 'object' ? data._systemId?.name : '') || '',
          shipments: nextShipments,
          totalAmount: data.totalInvoiceAmount || 0,
          invoiceNumber: data.invoiceNumber || '',
          _userId: data.user?._id,
          uniqueKey: data.uniqueKey,
        };
        return updateSelectedCreditDebitRowsList(prev, paramObj);
      });
    },
    [],
  );

  const totalSelectedShipmentsCount = useMemo(
    () => modalSelectedAccountRows.reduce((acc, row) => acc + (row.shipments?.length || 0), 0),
    [modalSelectedAccountRows],
  );

  const totalSelectedAmount = useMemo(
    () => calculateSelectedCreditDebitPricingSum(modalSelectedAccountRows, noteType),
    [modalSelectedAccountRows, noteType],
  );

  const handleModalSendTo365 = useCallback(() => {
    if (isNonAccount) {
      const shipmentIds = modalSelectedAccountRows.flatMap(r => r.shipments.map(s => s._id));
      const shipmentNumbers = modalSelectedAccountRows.flatMap(r => r.shipments.map(s => s.shipmentNumber));
      const formattedInvoiceDate = modalConfig.invoiceDate ? modalConfig.invoiceDate.format('YYYY-MM-DD') : '';
      const formattedDueDate =
        noteType === 'debit'
          ? modalConfig.dueDate
            ? modalConfig.dueDate.format('YYYY-MM-DD')
            : formattedInvoiceDate
          : formattedInvoiceDate;
      const payload = {
        shipmentIds,
        shipmentNumbers,
        dueDate: formattedDueDate,
        invoiceDate: formattedInvoiceDate,
        remarks: modalConfig.remarks || '',
      };
      if (isCredit) {
        dispatch(sendNonAccountCreditDataTo365(payload));
      } else {
        dispatch(sendNonAccountDebitDataTo365(payload));
      }
    } else {
      const payload = buildCreditDebitD365Payload(modalConfig, modalSelectedAccountRows, noteType);
      if (isCredit) {
        dispatch(sendAccountCreditDataTo365(payload));
      } else {
        dispatch(sendAccountDebitDataTo365(payload));
      }
    }
  }, [isNonAccount, modalConfig, modalSelectedAccountRows, noteType, isCredit, dispatch]);

  const handleModalSubmit = useCallback(() => {
    onSubmitNote(modalConfig, modalSelectedAccountRows, () => {
      onClose();
    });
  }, [onSubmitNote, modalConfig, modalSelectedAccountRows, onClose]);

  const missingFields = useMemo(
    () => validateCreditDebitConfig(modalConfig, modalSelectedAccountRows, noteType),
    [modalConfig, modalSelectedAccountRows, noteType],
  );

  const isConfigIncomplete = missingFields.length > 0;
  const isBusy = listLoading || loadingD365 || isSubmitting;
  const isCreateDisabled = isBusy || isConfigIncomplete;

  const disabledTooltipContent = useMemo(() => {
    if (isSubmitting) {
      return `Creating ${noteTitle.toLowerCase()}, please wait...`;
    }
    if (listLoading || loadingD365) {
      return 'Loading data, please wait...';
    }
    if (missingFields.length > 0) {
      return (
        <Box sx={{ p: 0.5, maxWidth: 300 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 0.5, color: 'inherit' }}>
            Cannot create {noteTitle.toLowerCase()}. Required field(s) missing:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.75rem', lineHeight: 1.5 }}>
            {missingFields.map(field => (
              <li key={field}>{field}</li>
            ))}
          </Box>
        </Box>
      );
    }
    return null;
  }, [isSubmitting, listLoading, loadingD365, missingFields, noteTitle]);

  return (
    <Modal
      open={open}
      onClose={handleCloseModalClean}
      maxWidth="xl"
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '1480px',
          height: { xs: '96dvh', sm: '94dvh', md: '92dvh' },
          maxHeight: '95dvh',
          m: { xs: 1, sm: 2, md: 2.5 },
          borderRadius: '18px',
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
      dialogContainerSx={{
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, sm: 2.5 },
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '18px',
        overflow: 'hidden',
      }}
    >
      {/* Top Header Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.5,
          pb: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ReceiptLongOutlinedIcon />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: '1.15rem', color: 'text.primary', lineHeight: 1.2 }}
            >
              Create {modalTitlePrefix} {noteTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.25 }}>
              Select unbilled invoices on the left and configure note parameters on the right
            </Typography>
          </Box>
        </Stack>

        {/* Live KPI Strip & Close Control */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          sx={{ width: { xs: '100%', md: 'auto' }, justifyContent: 'flex-end' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 0.75,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 600 }}>
                QUEUED NOTES
              </Typography>
              <Typography sx={{ fontSize: '0.96rem', fontWeight: 700, color: 'text.primary' }}>
                {modalSelectedAccountRows.length}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ height: 24, my: 'auto' }} />
            <Box>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 600 }}>SHIPMENTS</Typography>
              <Typography sx={{ fontSize: '0.96rem', fontWeight: 700, color: 'text.primary' }}>
                {totalSelectedShipmentsCount}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ height: 24, my: 'auto' }} />
            <Box>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 600 }}>
                TOTAL AMOUNT
              </Typography>
              <Tooltip
                title={`Total Amount: ${orgCurrency} ${formatNumber(totalSelectedAmount, locale)}`}
                arrow
                placement="top"
              >
                <Typography sx={{ fontSize: '0.96rem', fontWeight: 800, color: 'primary.main' }}>
                  {orgCurrency} {formatNumber(totalSelectedAmount, locale)}
                </Typography>
              </Tooltip>
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={handleCloseModalClean}
            sx={{
              color: 'text.secondary',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px',
              p: 0.75,
              '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.05) },
            }}
            aria-label="Close modal"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Main Dual Workspace Body */}
      <Box sx={{ flex: 1, minHeight: 0, py: 1.5, overflow: 'hidden' }}>
        <Grid container sx={{ height: '100%', minHeight: 0 }} spacing={0}>
          {/* Left Workspace: Shipments */}
          <Grid
            item
            xs={12}
            lg={7.5}
            sx={{
              height: { xs: 'auto', lg: '100%' },
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              pr: { lg: 1.75 },
              borderRight: { lg: `1px solid ${theme.palette.divider}` },
            }}
          >
            <CreditDebitShipmentSection
              noteType={noteType}
              list={list}
              totalCount={totalCount}
              currentPage={currentPage}
              perPage={perPage}
              loading={listLoading}
              activeSystemList={activeSystemList}
              searchFilterList={isNonAccount ? NonAccountCreditDebitSearchFilterList : CreditDebitSearchFilterList}
              filterSelectValue={modalFilterSelectValue}
              filterText={modalFilterText}
              dateRange={modalDateRange}
              system={modalSystem}
              selectedAccountRows={modalSelectedAccountRows}
              expandedInvoices={expandedInvoices}
              primaryCurrencySymbol={orgCurrency}
              onSearchSelectChange={onModalSearchSelectChange}
              onSearchTextChange={onModalSearchTextChange}
              onSearchTextSubmit={onModalSearchTextSubmit}
              onTextClear={onModalTextClear}
              onDateChange={onModalDateChange}
              onSystemFilter={onModalSystemFilter}
              onClearFilters={onModalClear}
              onPageChange={onModalPageChange}
              onRowsPerPageChange={onModalRowsPerPageChange}
              onSelectAllInvoices={handleSelectAllInvoicesInModal}
              onSelectInvoiceGroup={handleSelectInvoiceGroup}
              onToggleSingleShipment={handleToggleSingleShipment}
              onToggleExpandInvoice={toggleExpandInvoice}
            />
          </Grid>

          {/* Right Workspace: Config & Preview */}
          <Grid
            item
            xs={12}
            lg={4.5}
            sx={{
              height: { xs: 'auto', lg: '100%' },
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              p: '0 !important',
              pl: { lg: '14px !important' },
            }}
          >
            <CreditDebitConfigSection
              noteType={noteType}
              config={modalConfig}
              setConfig={setModalConfig}
              isWyngsMy={isWyngsMy}
              selectedAccountRows={modalSelectedAccountRows}
              totalSelectedShipmentsCount={totalSelectedShipmentsCount}
              totalSelectedAmount={totalSelectedAmount}
              primaryCurrencySymbol={orgCurrency}
              loadingD365={loadingD365}
              onSendTo365={handleModalSendTo365}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Bottom Sticky Modal Footer Actions */}
      <Box
        sx={{
          pt: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.82rem' }}>
          {modalSelectedAccountRows.length > 0
            ? `${
                modalSelectedAccountRows.length
              } ${noteTitle.toLowerCase()}(s) queued across ${totalSelectedShipmentsCount} shipment(s) • Total: ${orgCurrency} ${formatNumber(
                totalSelectedAmount,
                locale,
              )}`
            : `Select at least one invoice or shipment and set Invoice Date to create ${noteTitle.toLowerCase()}(s).`}
        </Typography>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <SbButton
            variant="outlined"
            className="rounded-full"
            sx={{ minWidth: 115, height: '40px !important' }}
            onClick={handleCloseModalClean}
            disabled={isSubmitting}
          >
            Cancel
          </SbButton>
          <Tooltip
            title={disabledTooltipContent || ''}
            arrow
            enterDelay={150}
            placement="top"
            disableHoverListener={!isCreateDisabled}
          >
            <span style={{ display: 'inline-flex' }}>
              <SbButton
                variant="contained"
                className="rounded-full"
                sx={{ minWidth: 160, height: '40px !important' }}
                onClick={handleModalSubmit}
                disabled={isCreateDisabled}
                loading={isSubmitting}
              >
                Create {noteTitle}
              </SbButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Modal>
  );
});

export default CreditDebitNoteModal;
